import { useState } from 'react'
import { type Car, CarStatus } from '../../../types'
import BannersAndToasts from '../components/BannersAndToasts'
import { ServiceCardBackside } from '../components/V3/Modals/ServiceCardBack'
import Dialog from '../../../components/modal/Dialog'
import { fetchCarByAccountNo, fetchCarByBudgetNo } from '../../../api/api'
import { useMutation, useQuery } from 'react-query'
import { AgreementDetails } from '../components/V3/AgreementDetails'
import { CarDetailsOverview } from '../components/V3/CarDetailsOverview'
import { CarMileage } from '../components/V3/CarMileage'
import { CarInsurance } from '../components/V3/CarInsurance'
import { AdditionalProducts } from '../components/V3/AdditionalProducts'
import { EstimatedEvents } from '../components/V3/EstimatedEvents'
import { CarInfoTable } from '../components/V3/CarInfoTable'
import { FooterV3 } from '../components/V3/FooterV3'
import { Loader } from '../components/V3/_shared/Loader'
import { ReportDamage } from '../components/V3/Modals/ReportDamage'
import { LostFueldCard } from '../components/V3/Modals/LostFuelCard'
import { EditDriver } from '../components/V3/Modals/EditDriver'
import { Visuals } from '../components/V3/Visuals'
import { EditMileage } from '../components/V3/Modals/EditMileage'
import { useChosenCompany, useUser } from '../../../context/UserProvider'
import { Box } from '../components/V3/_shared/Box'
import ServiceCardNew from '../v3/components/ServiceCardNew'
import { getCarImage, sendMessageEmail } from '../../../api/newApi'
import Textarea from '../components/V3/_shared/TextArea'
import { Check, Cross } from '../components/V3/icons'
import { SecondaryButton } from '../components/V3/_shared/Buttons/SecondaryButton'
import { useMediaQuery } from '../v3/hooks/useMediaQuery'

const CarCard = ({ user, type, id }) => {
  const comp = useChosenCompany()
  function fetchCarById() {
    if (type === 'account') {
      return fetchCarByAccountNo(user.user.id, comp?.id, id)
    }
    return fetchCarByBudgetNo(user.user.id, comp?.id, id)
  }

  const [showServiceCard, setShowServiceCard] = useState(false)
  const [showEditDriver, setShowEditDriver] = useState(false)
  const [showReportDamage, setShowReportDamage] = useState(false)
  const [showLostFuelCard, setShowLostFuelCard] = useState(false)
  const [showEditMileage, setShowEditMileage] = useState(false)
  const [showSendMessage, setShowSendMessage] = useState(false)
  const [additionalProductsOpen, setAdditionalProductsOpen] = useState(null)

  const modalSize = useMediaQuery('(min-width: 640px)')
  const largeModalSize = useMediaQuery('(min-width: 768px)')

  const {
    data: car,
    isError,
    isLoading,
  }: {
    data: Car
    isError: boolean
    isLoading: boolean
  } = useQuery(['car', id], fetchCarById)

  const carImageQuery = useQuery({
    enabled: !!car?.budgetNo && !!comp?.id,
    queryKey: ['car-image', car?.budgetNo, comp?.id],
    queryFn: () => getCarImage({ budgetNo: car?.budgetNo, companyId: comp?.id }),
  })

  if (isLoading) {
    return (
      <div className="w-full flex-col h-screen gap-2 flex items-center justify-center">
        <Loader />
        <p>Henter din bil...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="w-full h-screen flex-col gap-2 flex items-center justify-center">
        <p>Obs! Noe gikk galt da vi prøvde å hente din bil.</p>
        <a href="/cars" className="text-old-primary hover:underline cursor-pointer">
          Gå tilbake til bilparken
        </a>
      </div>
    )
  }
  if (!car) {
    return (
      <div className="w-full h-screen flex-col gap-2 flex items-center justify-center">
        <p>Vi klarte ikke hente data på din bil.</p>
        <a href="/cars" className="text-old-primary hover:underline cursor-pointer">
          Gå tilbake til bilparken
        </a>
      </div>
    )
  }

  return (
    <div className=" bg-[white]  items-center justify-center w-full hideScrollBar flex flex-col gap-8 relative">
      <BannersAndToasts car={car} />

      <div className="absolute hideScrollBar overflow-hidden h-screen w-full">
        <Dialog
          width={largeModalSize ? '800px' : '300px'}
          open={showSendMessage as any}
          //@ts-ignore
          onOpenChange={() => setShowSendMessage(!showSendMessage)}
        >
          <SendDirectMessage regNumber={car.registrationNumber || car.budgetNo || car.accountNo} />
        </Dialog>
        <Dialog
          width={modalSize ? '525px' : '300px'}
          open={showServiceCard as any}
          //@ts-ignore
          onOpenChange={() => setShowServiceCard(!showServiceCard)}
        >
          <ServiceCardBackside car={car} setShowServiceCard={setShowServiceCard} />
        </Dialog>
        <Dialog
          width={modalSize ? '525px' : '300px'}
          open={showEditDriver as any}
          //@ts-ignore
          onOpenChange={() => setShowEditDriver(!showEditDriver)}
        >
          <EditDriver setShowEditDriver={setShowEditDriver} id={id} car={car} user={user} />
        </Dialog>
        <Dialog
          width={modalSize ? '525px' : '300px'}
          open={showReportDamage as any}
          //@ts-ignore
          onOpenChange={() => setShowReportDamage(!showReportDamage)}
        >
          <ReportDamage id={id} />
        </Dialog>
        <Dialog
          width={largeModalSize ? '800px' : '300px'}
          open={showLostFuelCard as any}
          //@ts-ignore
          onOpenChange={() => setShowLostFuelCard(!showLostFuelCard)}
        >
          <LostFueldCard id={id} car={car} setShowLostFuelCard={setShowLostFuelCard} />
        </Dialog>
        <Dialog
          width={modalSize ? '525px' : '300px'}
          open={showEditMileage as any}
          //@ts-ignore
          onOpenChange={() => setShowEditMileage(!showEditMileage)}
        >
          <EditMileage setShowEditMileage={setShowEditMileage} id={id} car={car} />
        </Dialog>
      </div>

      <div className="relative w-full max-w-screen-xl grid grid-cols-1 px-12 pt-24 popUpFromBottom gap-8  z-[2]  hideScrollBar ">
        <div className="absolute z-[0] w-full h-full left-0">
          <Visuals />
        </div>
        <div className="flex flex-col-reverse md:flex-row justify-between [&>*]:h-full gap-[50px] w-full ">
          <AgreementDetails
            setShowLostFuelCard={setShowLostFuelCard}
            car={car}
            setShowSendMessage={setShowSendMessage}
            setShowEditDriver={setShowEditDriver}
          />
          <CarDetailsOverview
            carImageQuery={carImageQuery}
            budgetNo={car.budgetNo}
            vehicleType={car.vehicleType}
            car={car}
          />
        </div>
        <div className="grid  grid-cols-1 md:grid-cols-2 gap-8 w-full">
          <div className="flex flex-col gap-8">
            {type !== 'budget' && car.status !== CarStatus.Order && (
              <CarMileage setShowEditMileage={setShowEditMileage} car={car} />
            )}
            <CarInsurance setShowReportDamage={setShowReportDamage} showReportDamage car={car} id={id} />
            <EstimatedEvents car={car} />
          </div>
          <div className="flex flex-col gap-8 w-full">
            <Box title="Servicekort">
              {[CarStatus.Active, CarStatus.Administrated].includes(car?.status?.toLowerCase() as any) && (
                <ServiceCardNew showServiceCard={showServiceCard} setShowServiceCard={setShowServiceCard} car={car} />
              )}
            </Box>
            <AdditionalProducts
              car={car}
              showServiceCard={showServiceCard}
              setShowServiceCard={setShowServiceCard}
              setAdditionalProductsOpen={setAdditionalProductsOpen}
              additionalProductsOpen={additionalProductsOpen}
            />
          </div>
        </div>
        <CarInfoTable id={id} />
        <FooterV3 />
      </div>
    </div>
  )
}

export default CarCard

export function SendDirectMessage({ regNumber }: { regNumber: string }) {
  const [message, setMessage] = useState('')
  const company = useChosenCompany()
  const user = useUser().user
  const sendMessage = useMutation(sendMessageEmail, {
    onSuccess: () => {
      setMessage('')
    },
  })

  return (
    <div className="w-full flex flex-col gap-4 ">
      <p className="font-semibold text-2xl">Send direktemelding</p>
      <Textarea
        value={message}
        onChange={setMessage}
        id={'message'}
        label={'Melding'}
        placeholder="Skriv din melding her..."
      />
      {sendMessage.isSuccess && (
        <div className="bg-old-success-light border border-old-success-default rounded-lg p-4 flex items-start gap-3 text-old-success-default">
          <Check />
          <div className="flex-1">
            <h3 className="font-medium ">Melding sendt!</h3>
            <p className="text-sm  mt-1">Din melding har blitt sendt til Autoplan og vil bli behandlet snart.</p>
          </div>
        </div>
      )}
      {sendMessage.isError && (
        <div className="bg-[#ffe1e1] border border-old-content-danger  rounded-lg p-4 flex items-start gap-3 text-old-content-danger">
          <Cross />
          <div className="flex-1">
            <h3 className="font-medium ">Melding avbrutt!</h3>
            <p className="text-sm  mt-1">
              Prøv å send meldingen på nytt, eller kontakt Autoplan hvis problemet vedvarer.
            </p>
          </div>
        </div>
      )}
      <div className="flex h-10 items-center">
        {sendMessage.isLoading ? (
          <div className="flex items-center gap-2">
            <Loader />
            <span>Sender melding...</span>
          </div>
        ) : (
          <SecondaryButton
            disabled={!message.trim()}
            onClick={() => {
              sendMessage.mutate({
                company: company.name || '',
                registrationNumber: regNumber || '',
                name: user.name || '',
                mail: user.contactDetails.mail || '',
                phone: user.contactDetails.phone || '',
                address: user.contactDetails.address || '',
                postCode: user.contactDetails.postCode || '',
                city: user.contactDetails.city || '',
                message,
              })
            }}
          >
            Send melding
          </SecondaryButton>
        )}
      </div>
    </div>
  )
}
